import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { Alert, Button, CircularProgress, Divider, MenuItem, Select, Stack, Switch } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import OtaMappingRulesForm from "../../OtaMappingRulesForm";
import CasablancaRateMappingForm from "./CasablancaRateMappingForm";
import { CasablancaConfig } from "crqlar-react-common/dist/models/integrations/casablanca/casablancaConfig.model";
import { ProcessingStrategy } from "crqlar-react-common/dist/models/integrations/baseIntegrationConfig.model";
import {
  CasablancaMasterDataSyncResult,
  CasablancaService
} from "crqlar-react-common/dist/services/integrations/casablanca/casablanca.service";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  casablancaConfigData: UseEntityData<CasablancaConfig>;
}

const CasablancaConfigForm = ({ casablancaConfigData }: Props) => {

  const { t } = useTranslation();


  const casablancaConfig = casablancaConfigData.item;
  const nameMissing = isRequiredTextMissing(casablancaConfigData, casablancaConfig.name);
  const setCasablancaConfig = casablancaConfigData.setItem;

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<CasablancaMasterDataSyncResult | null>(null);

  //region Handlers

  const updateField = (field: keyof CasablancaConfig, value: any) => {
    setCasablancaConfig({ ...casablancaConfig, [field]: value });
  };

    const syncMasterData = async () => {
        if (!casablancaConfig.id) return
        setSyncing(true)
        setSyncResult(null)
        try {
            const result = await CasablancaService.syncMasterData(casablancaConfig.id)
            setSyncResult(result)
        } catch (e: any) {
            setSyncResult({
                success: false,
                outletPushed: false,
                cashdeskPushed: false,
                waiterPushed: false,
                commodityGroups: [],
                errors: [],
                message: e?.response?.data?.message || e?.message || t('Failed to sync POS master data') as string
            })
        } finally {
            setSyncing(false)
        }
    }

  //endregion Handlers

  //region UI

  const integrationInstructions = `
All requests should have the following POS XML element:

<POS>
    <Source AgentDutyCode="CASABLANCA" ERSP_UserID="%OPTIONAL%">
        <RequestorID ID="${casablancaConfig.id}" MessagePassword="${casablancaConfig.authorizationToken}" />
    </Source>
</POS>

    `.trim();

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Name')}
        tooltip={t('Internal label for identifying this Casablanca integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={casablancaConfig.name || ''}
          error={nameMissing}
          onChange={(e) => updateField('name', e.target.value)}
          fullWidth
          required />

                </Property>

                {
      casablancaConfig.id ?
      <>
                            <Property
          title={t('Username')}
          tooltip={t('Generated username for authenticating Casablanca requests.')}>

                                <TextField
            id={'username'}
            size={'small'}
            value={casablancaConfig.id || ''}
            fullWidth
            disabled />

                            </Property>

                            <Property
          title={t('Password / Auth token')}
          tooltip={t('Generated token used as the password for Casablanca requests.')}>

                                <TextField
            id={'token'}
            size={'small'}
            value={casablancaConfig.authorizationToken || ''}
            fullWidth
            disabled />

                            </Property>
                        </> :

      <Alert
        variant={'outlined'}
        severity={'info'}
        sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Please save the Casablanca configuration first to see the integration details')}
                        </Alert>
      }
            </Stack>
        </Stack>;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Enabled')}
        tooltip={t('Turns the Casablanca integration on or off.')}>

                    <Switch
          id={'enabled'}
          checked={casablancaConfig.enabled}
          onChange={(e) => updateField('enabled', e.target.checked)} />

                </Property>

                <Property
        title={t('Processing strategy')}
        tooltip={t('Controls whether imported messages are processed immediately or queued.')}>

                    <Select
          value={casablancaConfig.processingStrategy || ProcessingStrategy.IMMEDIATE}
          onChange={(e) => updateField('processingStrategy', e.target.value)}
          fullWidth
          size={'small'}
          variant={'outlined'}>

                        {
          Object.values(ProcessingStrategy).map((type, index) =>
          <MenuItem
            key={index}
            value={type}
            id={`processing-strategy-${type}`}>

                                    {t(`Processing strategy ${type}`)}
                                </MenuItem>
          )
          }
                    </Select>
                </Property>

                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <TextField
            id={'name'}
            size={'small'}
            value={casablancaConfig.propertyId || ''}
            onChange={(e) => updateField('propertyId', e.target.value)}
            fullWidth
            required />

                    </Stack>
                </Property>

                <OtaMappingRulesForm value={casablancaConfig.otaMappingRules}
      onChange={(r) => updateField('otaMappingRules', r)} />

                <CasablancaRateMappingForm
      casablancaConfig={casablancaConfig}
      onChange={(r) => updateField('rateMappingConfig', r)} />
            </Stack>
        </Stack>;


  const posBillingSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('POS Billing — Charge to Room')}
            </Typography>

            <Stack gap={1}>
                <Alert
        variant={'outlined'}
        severity={'info'}
        sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                    {t('These credentials are required to post Spa charges directly to a guest\'s hotel room via the Casablanca POS API. Get the values from your Casablanca partner (POS interface configuration).')}
                </Alert>

                <Property
        title={t('POS Base URL')}
        tooltip={t('Base URL of the Casablanca POS API.')}>

                    <TextField
          size={'small'}
          value={casablancaConfig.posBaseUrl || ''}
          onChange={(e) => updateField('posBaseUrl', e.target.value)}
          fullWidth
          placeholder={'http://localhost:5000'} />

                </Property>

                <Property
        title={t('POS Tenant ID')}
        tooltip={t('Tenant identifier for the Casablanca POS API.')}>

                    <TextField
          size={'small'}
          value={casablancaConfig.posTenantId || ''}
          onChange={(e) => updateField('posTenantId', e.target.value)}
          fullWidth
          placeholder={'e.g. hotel-arpuria'} />

                </Property>

                <Property
        title={t('POS Interface ID')}
        tooltip={t('Interface identifier for the Casablanca POS API.')}>

                    <TextField
          size={'small'}
          value={casablancaConfig.posInterfaceId || ''}
          onChange={(e) => updateField('posInterfaceId', e.target.value)}
          fullWidth
          placeholder={'e.g. pos-interface-1'} />

                </Property>

                <Property
        title={t('POS API Key')}
        tooltip={t('API key generated in the Casablanca partner portal.')}>

                    <TextField
          size={'small'}
          type={'password'}
          autoComplete={'new-password'}
          value={casablancaConfig.posApiKey || ''}
          onChange={(e) => updateField('posApiKey', e.target.value)}
          fullWidth
          placeholder={t('Generated in the Casablanca partner portal') as string} />

                </Property>

                <Property
        title={t('POS master data')}
        tooltip={t('Pushes the POS master data (outlet, cashdesk, waiter and one commodity group per SPA service category) to Casablanca.')}>

                    <Stack width={'100%'} gap={1}>
                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Pushes the POS master data (outlet, cashdesk, waiter and one commodity group per SPA service category) to Casablanca. Required during setup before charges can be posted. Save the configuration first; safe to run repeatedly.')}
                        </Alert>

                        <Button
            variant={'outlined'}
            onClick={syncMasterData}
            disabled={!casablancaConfig.id || syncing}
            startIcon={syncing ? <CircularProgress size={16} /> : undefined}
            sx={{ alignSelf: 'flex-start' }}>

                            {t('Sync POS master data')}
                        </Button>

                        {
              syncResult &&
              <Alert severity={syncResult.success ? 'success' : 'error'}>
                                <Typography>
                                    {syncResult.message
                  || (syncResult.success ? t('POS master data synced') : t('Failed to sync POS master data'))}
                                </Typography>
                                {
                  syncResult.errors?.length > 0 &&
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                                        {syncResult.errors.map((err, i) =>
                    <li key={i}><Typography variant={'caption'}>{err}</Typography></li>
                    )}
                                    </ul>
                  }
                            </Alert>
              }
                    </Stack>
                </Property>
            </Stack>
        </Stack>;


  const instructionsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Integration instructions')}
            </Typography>

            <Stack gap={1}>
                <Alert
        variant={'outlined'}
        severity={'info'}
        sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                    <Typography>
                        {t("Send")} <strong>{t("POST")}</strong> with XML body
                        to <strong>{process.env.REACT_APP_API_ENDPOINT}{t("/v3/integrations/casablanca/ota")}</strong>
                    </Typography>
                </Alert>

                <Alert
        variant={'outlined'}
        severity={'info'}
        sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                    <Typography sx={{
          whiteSpace: 'pre-wrap'
        }}>
                        {integrationInstructions}
                    </Typography>
                </Alert>
            </Stack>
        </Stack>;


  //endregion UI

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
            <Divider />
            {posBillingSection}

            {
      !!casablancaConfig.id &&
      <>
                    <Divider />
                    {instructionsSection}
                </>
      }
        </Stack>);


};

export default CasablancaConfigForm;
